import { getAutogeneratedBanner } from '../banners';
import {
  assertNoFieldNameCollisions,
  BLOB_ENCRYPTED_MSG,
  BLOB_MISSING_MSG,
  ENCRYPTED_BLOB_PREFIX,
  escapeBlockCommentEnd,
  getEnumMemberKind,
  getFieldDocLines,
  getSensitiveKeys,
  IDENTIFIER_SAFE_KEY,
  partitionRepresentableKeys,
  skippedKeysComment,
  type CoercedType,
  type ResolvedFieldType,
} from '../shared';

/** SCREAMING_SNAKE / snake → camelCase (`DB_HOST` → `dbHost`). */
export function snakeToCamelCase(key: string): string {
  const parts = key.split('_').filter((p) => p.length > 0);
  if (!parts.length) return key;
  return parts
    .map((part, i) => {
      const lower = part.toLowerCase();
      if (i === 0) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

// a Java field / local must be a non-keyword identifier; digit-leading camelCase (from `_2FA_…`) is invalid
const JAVA_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

const JAVA_KEYWORDS = new Set([
  'abstract',
  'assert',
  'boolean',
  'break',
  'byte',
  'case',
  'catch',
  'char',
  'class',
  'const',
  'continue',
  'default',
  'do',
  'double',
  'else',
  'enum',
  'extends',
  'final',
  'finally',
  'float',
  'for',
  'goto',
  'if',
  'implements',
  'import',
  'instanceof',
  'int',
  'interface',
  'long',
  'native',
  'new',
  'package',
  'private',
  'protected',
  'public',
  'return',
  'short',
  'static',
  'strictfp',
  'super',
  'switch',
  'synchronized',
  'this',
  'throw',
  'throws',
  'transient',
  'try',
  'void',
  'volatile',
  'while',
  // literals / reserved
  'true',
  'false',
  'null',
]);

function isJavaReserved(name: string): boolean {
  return JAVA_KEYWORDS.has(name);
}

/** one or more identifiers joined by dots, e.g. `com.example` or `Env` */
const JAVA_PACKAGE = /^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/;

type JavaTypeKind = 'long' | 'double' | 'boolean' | 'String' | 'Map' | 'List' | 'Object';

function javaTypeKind(coerced: CoercedType): JavaTypeKind {
  if (coerced === 'int') return 'long';
  if (coerced === 'number') return 'double';
  if (coerced === 'boolean') return 'boolean';
  if (coerced === 'object') return 'Map';
  if (typeof coerced === 'object' && 'enum' in coerced && coerced.enum.length) {
    const kind = getEnumMemberKind(coerced.enum);
    if (kind === 'int') return 'long';
    if (kind === 'number') return 'double';
    if (kind === 'boolean') return 'boolean';
    if (kind === 'mixed') return 'Object';
  }
  if (typeof coerced === 'object' && 'arrayOf' in coerced) return 'List';
  if (typeof coerced === 'object' && 'recordOf' in coerced) return 'Map';
  return 'String';
}

/** Field type as written on the class (boxed when optional so null is representable). */
function javaFieldType(kind: JavaTypeKind, isRequired: boolean): string {
  if (kind === 'Map') return 'Map<String, Object>';
  if (kind === 'List') return 'List<Object>';
  if (kind === 'Object') return 'Object';
  if (kind === 'String') return 'String';
  if (isRequired) {
    if (kind === 'long') return 'long';
    if (kind === 'double') return 'double';
    return 'boolean';
  }
  if (kind === 'long') return 'Long';
  if (kind === 'double') return 'Double';
  return 'Boolean';
}

function javaStringLiteral(text: string): string {
  return JSON.stringify(text);
}

function fieldJavadoc(field: ResolvedFieldType): Array<string> {
  const docLines = getFieldDocLines(field);
  if (!docLines.length) return [];
  return [
    '  /**',
    ...docLines.map((line) => `   * ${escapeBlockCommentEnd(line)}`),
    '   */',
  ];
}

/** Statements that bind a local `{fieldName}` from `config` (Java has no throw-expression ternary). */
function javaLoadLocal(field: ResolvedFieldType): Array<string> {
  const name = snakeToCamelCase(field.key);
  const kind = javaTypeKind(field.coerced);
  const type = javaFieldType(kind, field.isRequired);
  const keyLit = javaStringLiteral(field.key);
  const entry = `${name}Entry`;
  const missing = `throw new IllegalStateException("varlock: missing required key ${field.key} in __VARLOCK_ENV");`;

  let readValue: string;
  if (kind === 'Map') readValue = `mapper.convertValue(${entry}.get("value"), MAP_TYPE)`;
  else if (kind === 'List') readValue = `mapper.convertValue(${entry}.get("value"), LIST_TYPE)`;
  else if (kind === 'Object') readValue = `mapper.treeToValue(${entry}.get("value"), Object.class)`;
  else if (kind === 'String') readValue = `${entry}.get("value").asText()`;
  else if (kind === 'long') readValue = `${entry}.get("value").asLong()`;
  else if (kind === 'double') readValue = `${entry}.get("value").asDouble()`;
  else readValue = `${entry}.get("value").asBoolean()`;

  return [
    `      ${type} ${name};`,
    `      JsonNode ${entry} = config.get(${keyLit});`,
    `      if (${entry} == null || !${entry}.has("value")) {`,
    ...(field.isRequired
      ? [`        ${missing}`]
      : [`        ${name} = null;`]),
    '      } else {',
    `        ${name} = ${readValue};`,
    '      }',
  ];
}

export function generateJavaEnvSrc(
  fields: Array<ResolvedFieldType>,
  opts?: { packageName?: unknown, className?: unknown },
): string {
  const { safe, skipped } = partitionRepresentableKeys(
    fields,
    (k) => {
      const name = snakeToCamelCase(k);
      return !JAVA_IDENTIFIER.test(name) || isJavaReserved(name);
    },
  );
  assertNoFieldNameCollisions(safe, snakeToCamelCase, 'Java');

  const className = opts?.className === undefined ? 'Env' : String(opts.className);
  if (!IDENTIFIER_SAFE_KEY.test(className) || isJavaReserved(className)) {
    throw new Error(`@generateJavaEnv - \`class\` must be a valid Java class name (got ${JSON.stringify(opts?.className)})`);
  }

  let packageName: string | undefined;
  if (opts?.packageName !== undefined && String(opts.packageName).trim() !== '') {
    packageName = String(opts.packageName).trim();
    if (!JAVA_PACKAGE.test(packageName) || packageName.split('.').some(isJavaReserved)) {
      throw new Error(`@generateJavaEnv - \`package\` must be a valid Java package name (got ${JSON.stringify(opts?.packageName)})`);
    }
  }

  const sensitiveKeys = getSensitiveKeys(fields);
  const needsMap = safe.some((f) => javaTypeKind(f.coerced) === 'Map');
  const needsList = safe.some((f) => javaTypeKind(f.coerced) === 'List');
  const sensitiveInit = sensitiveKeys.length
    ? `Set.of(${sensitiveKeys.map(javaStringLiteral).join(', ')})`
    : 'Set.of()';

  const src = [
    getAutogeneratedBanner('//'),
    ...(packageName ? ['', `package ${packageName};`] : []),
    '',
    'import com.fasterxml.jackson.databind.JsonNode;',
    'import com.fasterxml.jackson.databind.ObjectMapper;',
    ...(needsMap ? ['import com.fasterxml.jackson.databind.type.MapType;'] : []),
    ...(needsList ? ['import com.fasterxml.jackson.databind.type.CollectionType;'] : []),
    ...(needsMap || needsList ? ['import com.fasterxml.jackson.databind.type.TypeFactory;'] : []),
    ...(needsList ? ['import java.util.List;'] : []),
    ...(needsMap ? ['import java.util.Map;'] : []),
    'import java.util.Set;',
    '',
    ...skippedKeysComment(skipped, '//'),
    `public final class ${className} {`,
    '  /** Keys that hold sensitive values — use to build your own redaction / leak-scanning. */',
    `  public static final Set<String> SENSITIVE_KEYS = ${sensitiveInit};`,
    '',
    ...(needsMap ? [
      '  private static final MapType MAP_TYPE = TypeFactory.defaultInstance()',
      '      .constructMapType(java.util.LinkedHashMap.class, String.class, Object.class);',
      '',
    ] : []),
    ...(needsList ? [
      '  private static final CollectionType LIST_TYPE = TypeFactory.defaultInstance()',
      '      .constructCollectionType(java.util.ArrayList.class, Object.class);',
      '',
    ] : []),
    ...safe.flatMap((field) => {
      const name = snakeToCamelCase(field.key);
      const type = javaFieldType(javaTypeKind(field.coerced), field.isRequired);
      return [...fieldJavadoc(field), `  public final ${type} ${name};`];
    }),
    '',
    `  public ${className}(`,
    ...safe.map((field, i) => {
      const name = snakeToCamelCase(field.key);
      const type = javaFieldType(javaTypeKind(field.coerced), field.isRequired);
      const comma = i < safe.length - 1 ? ',' : '';
      return `      ${type} ${name}${comma}`;
    }),
    '  ) {',
    ...safe.map((field) => {
      const name = snakeToCamelCase(field.key);
      return `    this.${name} = ${name};`;
    }),
    '  }',
    '',
    '  /** Parse the __VARLOCK_ENV blob (injected by `varlock run`) into a typed Env. */',
    `  public static ${className} load() {`,
    '    String blob = System.getenv("__VARLOCK_ENV");',
    '    if (blob == null || blob.isEmpty()) {',
    `      throw new IllegalStateException(${javaStringLiteral(BLOB_MISSING_MSG)});`,
    '    }',
    `    if (blob.startsWith(${javaStringLiteral(ENCRYPTED_BLOB_PREFIX)})) {`,
    `      throw new IllegalStateException(${javaStringLiteral(BLOB_ENCRYPTED_MSG)});`,
    '    }',
    '    try {',
    '      ObjectMapper mapper = new ObjectMapper();',
    '      JsonNode config = mapper.readTree(blob).get("config");',
    '      if (config == null || !config.isObject()) {',
    '        throw new IllegalStateException("varlock: __VARLOCK_ENV missing config object");',
    '      }',
    ...safe.flatMap(javaLoadLocal),
    `      return new ${className}(`,
    ...safe.map((field, i) => {
      const comma = i < safe.length - 1 ? ',' : '';
      return `          ${snakeToCamelCase(field.key)}${comma}`;
    }),
    '      );',
    '    } catch (IllegalStateException e) {',
    '      throw e;',
    '    } catch (Exception e) {',
    '      throw new IllegalStateException("varlock: failed to parse __VARLOCK_ENV", e);',
    '    }',
    '  }',
    '}',
    '',
  ];

  return src.join('\n');
}
